import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FaCalculator, 
  FaExchangeAlt, 
  FaQrcode, 
  FaFileExport,
  FaChartBar,
  FaDatabase,
  FaCode,
  FaClipboard
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import './Tools.css';

function Tools() {
  const [selectedTool, setSelectedTool] = useState(null);
  const [calcInput, setCalcInput] = useState('');
  const [calcResult, setCalcResult] = useState('0');
  const [converterValue, setConverterValue] = useState('');
  const [converterFrom, setConverterFrom] = useState('EUR');
  const [converterTo, setConverterTo] = useState('USD');
  const [qrText, setQrText] = useState('');

  const tools = [
    {
      id: 'calculator',
      name: 'Kalkulačka',
      icon: FaCalculator,
      description: 'Jednoduchá kalkulačka pre rýchle výpočty',
      color: '#3b82f6'
    },
    {
      id: 'converter',
      name: 'Konvertor mien',
      icon: FaExchangeAlt,
      description: 'Prevod medzi rôznymi menami',
      color: '#10b981'
    },
    {
      id: 'qrcode',
      name: 'QR kód generátor',
      icon: FaQrcode,
      description: 'Vytvorenie QR kódov',
      color: '#8b5cf6'
    },
    {
      id: 'export',
      name: 'Export dát',
      icon: FaFileExport,
      description: 'Export dát do rôznych formátov',
      color: '#f59e0b'
    },
    {
      id: 'statistics',
      name: 'Štatistiky',
      icon: FaChartBar,
      description: 'Prehľad štatistík a grafov',
      color: '#ef4444'
    },
    {
      id: 'backup',
      name: 'Zálohovanie',
      icon: FaDatabase,
      description: 'Zálohovanie a obnova dát',
      color: '#06b6d4'
    },
    {
      id: 'api',
      name: 'API tester',
      icon: FaCode,
      description: 'Testovanie API endpointov',
      color: '#ec4899'
    },
    {
      id: 'clipboard',
      name: 'Schránka',
      icon: FaClipboard,
      description: 'Správa schránky a šablón',
      color: '#84cc16'
    }
  ];

  const handleCalculate = () => {
    try {
      const result = eval(calcInput);
      setCalcResult(result.toString());
    } catch (error) {
      setCalcResult('Chyba');
    }
  };

  const handleConvert = () => {
    const rates = {
      'EUR': 1,
      'USD': 1.09,
      'GBP': 0.86,
      'CZK': 24.5,
      'PLN': 4.3
    };
    
    const euroValue = parseFloat(converterValue) / rates[converterFrom];
    const result = euroValue * rates[converterTo];
    toast.success(`${converterValue} ${converterFrom} = ${result.toFixed(2)} ${converterTo}`);
  };

  const renderToolContent = () => {
    if (!selectedTool) {
      return (
        <div className="tools-grid">
          {tools.map((tool) => {
            const Icon = tool.icon;
            return (
              <motion.div
                key={tool.id}
                className="tool-card"
                onClick={() => setSelectedTool(tool)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{ borderTop: `4px solid ${tool.color}` }}
              >
                <div className="tool-icon" style={{ background: `${tool.color}20`, color: tool.color }}>
                  <Icon />
                </div>
                <h3>{tool.name}</h3>
                <p>{tool.description}</p>
              </motion.div>
            );
          })}
        </div>
      );
    }

    switch(selectedTool.id) {
      case 'calculator':
        return (
          <motion.div 
            className="tool-content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="tool-header">
              <h2><FaCalculator /> Kalkulačka</h2>
              <button 
                className="btn btn-secondary"
                onClick={() => setSelectedTool(null)}
              >
                Späť
              </button>
            </div>
            <div className="calculator">
              <input
                type="text"
                value={calcInput}
                onChange={(e) => setCalcInput(e.target.value)}
                placeholder="Zadajte výraz"
                className="calc-input"
              />
              <div className="calc-result">{calcResult}</div>
              <div className="calc-buttons">
                {['7','8','9','/','4','5','6','*','1','2','3','-','0','.','=','+'].map((btn) => (
                  <button
                    key={btn}
                    className={`calc-btn ${btn === '=' ? 'calc-btn-equals' : ''}`}
                    onClick={() => {
                      if (btn === '=') {
                        handleCalculate();
                      } else {
                        setCalcInput(calcInput + btn);
                      }
                    }}
                  >
                    {btn}
                  </button>
                ))}
                <button 
                  className="calc-btn calc-btn-clear"
                  onClick={() => {
                    setCalcInput('');
                    setCalcResult('0');
                  }}
                >
                  C
                </button>
              </div>
            </div>
          </motion.div>
        );

      case 'converter':
        return (
          <motion.div 
            className="tool-content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="tool-header">
              <h2><FaExchangeAlt /> Konvertor mien</h2>
              <button 
                className="btn btn-secondary"
                onClick={() => setSelectedTool(null)}
              >
                Späť
              </button>
            </div>
            <div className="converter">
              <div className="converter-row">
                <input
                  type="number"
                  value={converterValue}
                  onChange={(e) => setConverterValue(e.target.value)}
                  placeholder="Zadajte sumu"
                  className="converter-input"
                />
                <select 
                  value={converterFrom}
                  onChange={(e) => setConverterFrom(e.target.value)}
                  className="converter-select"
                >
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                  <option value="GBP">GBP</option>
                  <option value="CZK">CZK</option>
                  <option value="PLN">PLN</option>
                </select>
              </div>
              <div className="converter-arrow">
                <FaExchangeAlt />
              </div>
              <div className="converter-row">
                <select 
                  value={converterTo}
                  onChange={(e) => setConverterTo(e.target.value)}
                  className="converter-select"
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="CZK">CZK</option>
                  <option value="PLN">PLN</option>
                </select>
              </div>
              <button 
                className="btn btn-primary"
                onClick={handleConvert}
              >
                Konvertovať
              </button>
            </div>
          </motion.div>
        );

      case 'qrcode':
        return (
          <motion.div 
            className="tool-content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="tool-header">
              <h2><FaQrcode /> QR kód generátor</h2>
              <button 
                className="btn btn-secondary"
                onClick={() => setSelectedTool(null)}
              >
                Späť
              </button>
            </div>
            <div className="qr-generator">
              <textarea
                value={qrText}
                onChange={(e) => setQrText(e.target.value)}
                placeholder="Zadajte text pre QR kód"
                rows="4"
                className="qr-input"
              />
              <button 
                className="btn btn-primary"
                onClick={() => toast.success('QR kód vygenerovaný')}
              >
                Generovať QR kód
              </button>
              <div className="qr-preview">
                <div className="qr-placeholder">
                  <FaQrcode />
                  <p>QR kód sa zobrazí tu</p>
                </div>
              </div>
            </div>
          </motion.div>
        );

      default:
        return (
          <motion.div 
            className="tool-content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="tool-header">
              <h2>{React.createElement(selectedTool.icon)} {selectedTool.name}</h2>
              <button 
                className="btn btn-secondary"
                onClick={() => setSelectedTool(null)}
              >
                Späť
              </button>
            </div>
            <div className="tool-placeholder">
              <p>Táto funkcia bude čoskoro dostupná</p>
            </div>
          </motion.div>
        );
    }
  };

  return (
    <div className="tools-page">
      <div className="page-header">
        <h1>Nástroje</h1>
      </div>
      {renderToolContent()}
    </div>
  );
}

export default Tools;